<template>
    <div class="tr-wrapper new-hire-row" :class="rowClass">
        <div class="d-flex align-items-start p-2">
            <a href class="collapse-trigger mr-1" role="button" :class="{'is-expanded': expanded}" :aria-expanded="expanded ? 'true' : 'false'" @click.prevent="$emit('toggle')">
                <span class="widget-toggle-wrapper">
                    <collapse-toggle-button />
                </span>
                <span class="sr-only">Details for {{ name }}</span>
            </a>

            <div class="new-hire-name">
                <h4 class="mb-0">{{ name }}</h4>
                <div class="text-muted">Store {{ row.store }}</div>
                <div class="text-muted" v-for="note in row.notes" :key="note">{{ note }}</div>
                <small class="d-block text-danger" v-for="error in otherErrors" :key="error">{{ error }}</small>
            </div>

            <div class="new-hire-field" v-for="field in quickFields" :key="field.name" :style="{width: field.width}">
                <label class="theading mb-0" :for="inputId(field.name)">{{ field.label }}</label>
                <select v-if="field.options" class="form-control form-control-sm" :class="{'is-invalid': errorsFor(field.name).length}" :id="inputId(field.name)" v-model="local[field.name]" :disabled="row.skip" @change="save(field.name)">
                    <option v-if="field.blank" value=""></option>
                    <option v-for="option in field.options" :key="option.value" :value="option.value">{{ option.text }}</option>
                </select>
                <input v-else type="text" inputmode="decimal" class="form-control form-control-sm" :class="{'is-invalid': errorsFor(field.name).length, 'new-hire-highrate': highRate}" :id="inputId(field.name)" v-model="local[field.name]" :disabled="row.skip" @change="save(field.name)">
                <small class="d-block text-danger" v-for="error in errorsFor(field.name)" :key="error">{{ error }}</small>
                <small class="d-block text-warning-dark" v-if="field.name == 'pay_rate' && highRate">{{ row.warnings.pay_rate }}</small>
            </div>

            <div class="ml-auto new-hire-status">
                <div class="theading">Status</div>
                <div v-if="saveStatus == 'saving'" class="text-muted">Saving&hellip;</div>
                <div v-else-if="saveStatus == 'failed'" class="text-danger">
                    Not saved. <a href @click.prevent="$emit('retry')">Retry</a>
                </div>
                <div v-else-if="status == 'skipped'" class="text-muted">
                    Skipped
                    <small class="d-block">{{ row.hours.reg }} regular, {{ row.hours.ot }} OT</small>
                </div>
                <div v-else-if="status == 'attention'">
                    <span class="counter counter-warnings">
                        <i aria-hidden="true" class="counter-icon fas fa-fw fa-exclamation-triangle"></i>
                        <span class="counter-label">{{ issueCount }}</span>
                        <span class="sr-only">{{ issueCount == 1 ? 'issue' : 'issues' }}</span>
                    </span>
                </div>
                <div v-else>
                    <i aria-hidden="true" class="fas fa-fw fa-check mr-hf text-primary"></i>Ready
                </div>
            </div>

            <div class="new-hire-skip">
                <label class="theading mb-0" :for="inputId('skip')">Skip</label>
                <div>
                    <input type="checkbox" :id="inputId('skip')" :checked="row.skip" @change="$emit('change', {skip: $event.target.checked})">
                </div>
            </div>
        </div>

        <div class="new-hire-detail px-2 pb-2" v-if="expanded">
            <div class="d-flex flex-wrap" v-for="(fields, idx) in detailRows" :key="idx">
                <div class="new-hire-field" v-for="field in fields" :key="field.name" :style="{width: field.width}">
                    <label class="theading mb-0" :for="inputId(field.name)">{{ field.label }}</label>
                    <input class="form-control form-control-sm" :type="field.type || 'text'" :inputmode="field.inputmode" :maxlength="field.maxlength" autocomplete="off" :class="{'is-invalid': errorsFor(field.name).length}" :id="inputId(field.name)" v-model="local[field.name]" :disabled="row.skip" @change="save(field.name)">
                    <small class="d-block text-danger" v-for="error in errorsFor(field.name)" :key="error">{{ error }}</small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { displayName, quickFields } from './new-hires'

// The person, then how to reach them. Widths include the 0.75rem between
// fields, so the two rows share column edges.
const DETAIL_ROWS = [
    [
        {name: 'first_name', label: 'First name', width: '10rem'},
        {name: 'last_name', label: 'Last name', width: '11rem'},
        {name: 'date_of_birth', label: 'Date of birth', width: '10rem', type: 'date'},
        {name: 'ssn', label: 'SSN', width: '11rem', inputmode: 'numeric'},
        {name: 'date_hired', label: 'Hire date', width: '10rem', type: 'date'},
    ],
    [
        {name: 'address_1', label: 'Address', width: '21.75rem'},
        {name: 'city', label: 'City', width: '10rem'},
        {name: 'state', label: 'State', width: '4rem', maxlength: 2},
        {name: 'zip_code', label: 'ZIP', width: '6.25rem', maxlength: 10},
        {name: 'email', label: 'Email', width: '15rem', type: 'email'},
    ],
]
const DETAIL_FIELDS = DETAIL_ROWS.flat()

export default {
    props: {
        row: {type: Object, required: true},
        choices: {type: Object, required: true},
        saveStatus: {type: String, default: ''},
        expanded: {type: Boolean, default: false},
    },
    emits: ['toggle', 'change', 'retry'],
    data() {
        return {
            // what is in the inputs; the parent's copy only changes on save
            local: {...this.row.values},
            detailRows: DETAIL_ROWS,
        }
    },
    watch: {
        // a new values object means the server's values were taken (load, bulk change)
        'row.values'(values) {
            this.local = {...values}
        },
    },
    computed: {
        name() {
            return displayName(this.local)
        },
        status() {
            if (this.row.skip) {
                return 'skipped'
            }
            return this.row.status == 'skipped' ? 'ready' : this.row.status
        },
        rowClass() {
            return {
                'tr-hasissue issue-blocker': this.status == 'attention',
                'issue-nonblocker': this.status != 'attention',
                'new-hire-row-skipped': this.status == 'skipped',
            }
        },
        quickFields() {
            return quickFields(this.choices)
        },
        otherErrors() {
            const shown = this.quickFields.concat(DETAIL_FIELDS).map(field => field.name)
            return Object.keys(this.row.errors)
                .filter(field => !shown.includes(field))
                .flatMap(field => this.row.errors[field])
        },
        issueCount() {
            return Object.keys(this.row.errors).length
        },
        highRate() {
            return !!this.row.warnings.pay_rate && !this.errorsFor('pay_rate').length
        },
    },
    methods: {
        inputId(field) {
            return `new-hire-${this.row.idx}-${field}`
        },
        errorsFor(field) {
            return this.row.errors[field] || []
        },
        save(field) {
            this.$emit('change', {values: {[field]: this.local[field]}})
        },
    },
}
</script>

<style>
    /* Column widths, unscoped: the "set for all" row in POSIntegrationNewHires lines up on them */
    .new-hire-name {
        width: 15rem;
        flex-shrink: 0;
    }

    .new-hire-field {
        margin-right: 0.75rem;
        margin-bottom: 0.25rem;
        flex-shrink: 0;
    }

    .new-hire-status {
        width: 11rem;
        flex-shrink: 0;
    }

    .new-hire-skip {
        width: 4.5rem;
        flex-shrink: 0;
        white-space: nowrap;
    }
</style>

<style scoped>
    .new-hire-detail {
        padding-left: 2.25rem !important;
    }

    .new-hire-detail .new-hire-field {
        margin-bottom: 0.5rem;
    }

    .new-hire-row-skipped .new-hire-name,
    .new-hire-row-skipped .new-hire-field {
        opacity: 0.5;
    }

    .new-hire-highrate {
        border-color: #DEBB00;
        background-color: #FBF4CC;
    }
</style>
